<template>
  <div v-loading="loading" class="account-center">
    <!-- 余额显隐只影响金额文本，不修改接口数据，便于在公共场景临时隐藏敏感金额。 -->
    <div class="account-toolbar">
      <el-button size="small" plain @click="balanceVisible = !balanceVisible">
        <i :class="balanceVisible ? 'el-icon-view' : 'el-icon-lock'"></i>
        {{ balanceVisible ? $t('message.hideAccountBalance') : $t('message.showAccountBalance') }}
      </el-button>
    </div>

    <!--
      接口将账户分为 china_accounts 和 japan_accounts；页面统一转换为 accountGroups 后循环渲染。
      group.key 同时用于生成区域样式类，便于对日本账户的单行布局做独立控制。
    -->
    <div v-for="group in accountGroups" :key="group.key" class="account-section"
      :class="`account-section-${group.key}`">
      <div class="section-heading">
        <h2>{{ group.title }}</h2>
        <span class="account-count">{{ group.accounts.length }}{{ $t('message.accountCenterAccountUnit') }}</span>
      </div>

      <div v-if="group.accounts.length" class="account-grid">
        <!-- 余额超过两项的平台使用宽卡片，当前对应 PingPong 的多账户余额结构。 -->
        <article v-for="account in group.accounts" :key="account.platform" class="account-card"
          :class="{ 'wide-card': account.balances.length > 2 }">
          <div class="account-card-header">
            <!-- platform 与本地 Balance 素材映射；未配置图片时使用接口返回的平台名称兜底。 -->
            <div class="platform-brand" :class="`platform-${account.platform}`">
              <span v-if="platformLogo(account.platform)" class="platform-logo-box">
                <img class="platform-logo" :src="platformLogo(account.platform)" :alt="account.name">
              </span>
              <strong v-else>{{ account.name }}</strong>
            </div>
            <!-- 状态标签展示 status_name；同步错误信息仅在状态标签悬停时展示，避免占用卡片空间。 -->
            <el-tooltip :disabled="!accountErrors(account).length" placement="top">
              <div slot="content" class="account-error-list">
                <p v-for="(message, index) in accountErrors(account)" :key="index">{{ message }}</p>
              </div>
              <el-tag class="account-status" size="small" :type="statusTagType(account.status)">
                {{ account.status_name }}
              </el-tag>
            </el-tooltip>
          </div>

          <!--
            multiple-balances：启用多余额网格；three-balances：启用左侧两项、右侧一项的专用布局。
            balance_type + currency + index 共同作为 key，兼容同一平台下不同余额类型和币种。
          -->
          <div class="balance-list" :class="{
            'multiple-balances': account.balances.length > 1,
            'three-balances': account.balances.length === 3,
          }">
            <div v-for="(balance, index) in account.balances" :key="`${balance.balance_type}-${balance.currency}-${index}`"
              class="balance-item">
              <div class="balance-label">
                <!-- CNY/JPY 使用本地国旗图标，未配置的币种不渲染占位图片。 -->
                <img v-if="currencyFlag(balance.currency)" class="currency-flag" :src="currencyFlag(balance.currency)"
                  :alt="balance.currency">
                <span>{{ balanceLabel(balance) }}</span>
              </div>
              <div class="balance-value">
                <span class="currency-symbol">{{ currencySymbol(balance.currency) }}</span>
                <span>{{ balanceVisible ? formatBalance(balance.balance) : '******' }}</span>
              </div>
            </div>
          </div>

          <!-- 优先显示余额实际更新时间；尚未同步成功时显示最后尝试时间。 -->
          <footer class="account-card-footer">
            <span>{{ $t('message.accountBalanceUpdatedAt') }}</span>
            <span>{{ account.updated_at || account.last_attempt_at || '-' }}</span>
          </footer>
        </article>
      </div>
      <div v-else class="No_data">{{ $t('message.noDataYet') }}</div>
    </div>
  </div>
</template>

<script>
import worldfirstLogo from '@/assets/Balance/wlh.png'
import payoneerLogo from '@/assets/Balance/Payoneer.png'
import pingpongLogo from '@/assets/Balance/pingpong.png'
import alipayLogo from '@/assets/Balance/zfb.png'
import rakutenLogo from '@/assets/Balance/Rakuten.png'
import paypayLogo from '@/assets/Balance/paypay.png'
import yuchoLogo from '@/assets/Balance/jp.png'
import paypalLogo from '@/assets/Balance/paypal.png'
import chinaFlag from '@/assets/Balance/china.svg'
import japanFlag from '@/assets/Balance/jp.svg'

/**
 * 接口 platform 字段与本地平台 Logo 的固定映射。
 * 图片统一从 src/assets/Balance 读取，避免接口名称变化影响品牌图片展示。
 */
const platformLogos = {
  worldfirst: worldfirstLogo,
  payoneer: payoneerLogo,
  pingpong: pingpongLogo,
  alipay: alipayLogo,
  rakuten: rakutenLogo,
  paypay: paypayLogo,
  yucho: yuchoLogo,
  paypal: paypalLogo,
}

/**
 * 接口 currency 字段与国家图标的固定映射。
 * 当前余额接口只包含人民币和日元，后续增加币种时在此补充对应素材即可。
 */
const currencyFlags = {
  CNY: chinaFlag,
  JPY: japanFlag,
}

export default {
  name: 'AccountBalanceCenter',
  data() {
    return {
      // 接口请求期间显示 Element UI loading，结束或失败后统一关闭。
      loading: false,
      // 默认展示真实余额；切换后仅将金额替换为星号，不清空原始数据。
      balanceVisible: true,
      // 分别保存接口 data.china_accounts 与 data.japan_accounts 的原始账户数组。
      chinaAccounts: [],
      japanAccounts: [],
    }
  },
  computed: {
    /**
     * 将中日账户整理为统一的渲染结构。
     * key 用于稳定循环和区域样式类，title 使用当前语言环境的 i18n 文案。
     */
    accountGroups() {
      return [
        {
          key: 'china',
          title: this.$t('message.chinaAccounts'),
          accounts: this.chinaAccounts,
        },
        {
          key: 'japan',
          title: this.$t('message.japanAccounts'),
          accounts: this.japanAccounts,
        },
      ]
    },
  },
  created() {
    // 进入账户中心时立即获取一次最新账户余额。
    this.getAccountBalances()
  },
  methods: {
    /**
     * 获取账户中心余额。
     * 接口成功时直接保存 china_accounts、japan_accounts，不额外改写后端字段；
     * 业务错误使用接口 msg 提示，loading 在 Promise 完成后统一关闭。
     */
    getAccountBalances() {
      this.loading = true
      this.$api.financialApi.accountBalanceList({}).then((res) => {
        if (res.code != 0) return this.$message.error(res.msg)
        this.chinaAccounts = res.data.china_accounts
        this.japanAccounts = res.data.japan_accounts
      }).finally(() => {
        this.loading = false
      })
    },
    /** 根据账户 platform 返回对应的本地 Logo；无映射时由模板展示 account.name。 */
    platformLogo(platform) {
      return platformLogos[platform]
    },
    /** 根据余额 currency 返回国家图标；无映射时模板不展示图片。 */
    currencyFlag(currency) {
      return currencyFlags[currency]
    },
    /**
     * 将接口状态转换为 Element UI 标签类型：
     * normal=绿色、error=红色，pending 等其他状态统一使用灰色。
     */
    statusTagType(status) {
      if (status === 'normal') return 'success'
      if (status === 'error') return 'danger'
      return 'info'
    },
    /**
     * 生成余额标题。
     * 普通余额和资金账户直接显示币种名称；预入账等特殊账户显示接口 balance_type_name。
     */
    balanceLabel(balance) {
      const currencyNames = {
        CNY: this.$t('message.accountCurrencyCny'),
        JPY: this.$t('message.accountCurrencyJpy'),
      }
      const currencyName = currencyNames[balance.currency] || balance.currency
      if (balance.balance_type === 'normal' || balance.balance_type === 'fund_account') {
        return `${currencyName}（${balance.currency}）`
      }
      return `${balance.balance_type_name}（${balance.currency}）`
    },
    /** 人民币和日元均使用 ¥；其他币种暂时直接显示币种代码。 */
    currencySymbol(currency) {
      return ['CNY', 'JPY'].includes(currency) ? '¥' : currency
    },
    /**
     * 将接口金额统一格式化为千分位和两位小数。
     * null、空字符串表示尚未获取余额，显示 --；非数字内容保持接口原值，避免误导。
     */
    formatBalance(balance) {
      if (balance === null || balance === '') return '--'
      const amount = Number(balance)
      if (Number.isNaN(amount)) return balance
      return amount.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    /** 收集并去重账户下各余额项的同步错误，供状态标签 Tooltip 展示。 */
    accountErrors(account) {
      return [...new Set(account.balances.map(item => item.error_message).filter(Boolean))]
    },
  },
}
</script>

<style lang="scss" scoped>
// 账户中心内容区保留最小高度，避免接口加载和空数据时页面高度跳动。
.account-center {
  min-height: 360px;
}

// 余额显隐工具栏固定靠右，按钮样式与账户中心浅色卡片风格保持一致。
.account-toolbar {
  display: flex;
  justify-content: flex-end;


  .el-button {
    height: 34px;
    padding: 0 14px;
    border-color: #c6e2ff;
    border-radius: 6px;
    background-color: #fff;
    box-shadow: 0 2px 6px rgba(64, 158, 255, 0.08);
    color: #303133;
    font-size: 14px;
    font-weight: 600;

    &:hover,
    &:focus {
      border-color: #409eff;
      background-color: #ecf5ff;
      color: #303133;
    }
  }

  i {
    margin-right: 6px;
    color: #409eff;
    font-size: 16px;
  }
}

// 中国账户、日本账户是两个独立区块，区块间保留纵向间距。
.account-section {
  margin-bottom: 36px;
}

// 分组标题由区域名称和账户数量徽标组成。
.section-heading {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;

  h2 {
    margin: 0;
    color: #303133;
    font-size: 26px;
    font-weight: 700;
    line-height: 36px;
  }
}

.account-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 14px;
  border-radius: 16px;
  background-color: #ecf5ff;
  color: #036EB8;
  font-size: 14px;
  font-weight: 700;
  line-height: 32px;
  box-sizing: border-box;
}

// 账户卡片使用 Flex 换行；具体宽度和间距由 account-card 控制。
.account-grid {
  display: flex;
  flex-wrap: wrap;
  margin-right: 20px;
  &:nth-last-child(1){
    margin-right: 0;
  }
}

// 普通账户卡片宽 392px；余额超过两项的宽卡片单独扩展至 660px。
.account-card {
  display: flex;
  width: 392px;
  max-width: 100%;
  margin-right: 20px;
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 4px 14px rgba(31, 45, 61, 0.1);
  flex-direction: column;
  box-sizing: border-box;

  &.wide-card {
    grid-column: span 2;
    width: 660px;
  }
}

// 日本账户卡片略微收窄并禁止换行，使四个日本账户在桌面端保持同一行。
.account-section-japan {
  .account-grid {
    flex-wrap: nowrap;
  }

  .account-card {
    flex: none;
    width: 370px;
  }

  .balance-item {
    padding-top: 0;
  }
}

// 卡片头部承载平台 Logo 与同步状态，固定高度保证不同 Logo 的卡片对齐。
.account-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 20px 30px 30px;
  overflow: hidden;
}

// 同步状态使用固定尺寸，避免不同状态文案导致卡片头部抖动。
.account-status {
  width: 86px;
  height: 32px;
  line-height: 30px;
  text-align: center;
}

// 平台名称仅作为无 Logo 时的兜底，过长文本使用省略号处理。
.platform-brand {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  color: #303133;
  font-size: 20px;

  strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// 所有平台图片使用统一的 240×48 可视区域。
.platform-logo-box {
  display: flex;
  flex: none;
  align-items: center;
  width: 240px;
  height: 48px;
  overflow: hidden;
}

.platform-logo {
  display: block;
  width: 240px;
  height: 48px;
  object-fit: contain;
  object-position: left center;
}

// Payoneer 素材右侧含静态状态图，仅截取 Logo，状态仍按接口实时展示。
.platform-payoneer .platform-logo-box {
  .platform-logo {
    width: auto;
    max-width: none;
  }
}

// 默认余额列表为单列；宽卡片存在多个余额时切换为两列网格。
.balance-list {
  display: grid;
  flex: 1;
  grid-template-columns: 1fr;
  padding: 0 30px 18px;
}

.wide-card .balance-list.multiple-balances {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

// 每个余额项通过伪元素生成横向分隔线，最后一项不显示分隔线。
.balance-item {
  position: relative;
  min-width: 0;
  padding: 20px 18px 0;

  &:not(:last-child)::before {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 1px;
    background-color: #ebeef5;
    content: '';
  }
}

// 三项余额采用左右布局：左侧两项上下排列，右侧一项跨两行，中央边框保持连续。
.wide-card .balance-list.three-balances .balance-item:nth-child(2) {
  grid-column: 2;
  grid-row: 1 / span 2;
  margin-left: 20px;
  border-left: 1px solid #ebeef5;

  &::before {
    display: none;
  }
}

// 币种名称与国家图标横向排列。
.balance-label {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #606266;
  font-size: 14px;
  span{
    margin-left: 8px;
  }
}

// 国家图标统一使用 32×32，避免不同格式素材导致尺寸不一致。
.currency-flag {
  display: block;
  flex: none;
  width: 32px;
  height: 32px;
  object-fit: contain;
}

// 金额与货币符号基线对齐，金额固定两位小数后保持单行展示。
.balance-value {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 10px;
  color: #303133;
  font-size: 24px;
  font-weight: 700;
  white-space: nowrap;
  padding-bottom: 20px;
  padding-left: 52px;
}

.currency-symbol {
  font-size: 20px;
}

// 卡片底部展示更新时间，使用浅灰背景与余额内容区区分。
.account-card-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16.5px 20px;
  background-color: #f5f7fa;
  color: #606266;
  font-size: 13px;
}

// 同一账户可能有多个余额同步错误，Tooltip 内纵向排列并限制最大宽度。
.account-error-list {
  max-width: 420px;

  p {
    margin: 0 0 6px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// 接口对应区域没有账户时沿用项目通用空数据样式。
.No_data {
  padding: 40px 0;
  color: #909399;
  text-align: center;
}

// 中等屏幕下保留原有卡片列数兼容规则。
@media (max-width: 1500px) {
  .account-grid {
    grid-template-columns: repeat(3, minmax(260px, 1fr));
  }
}

// 较窄桌面下减少账户列数，防止普通卡片相互覆盖。
@media (max-width: 1100px) {
  .account-grid {
    grid-template-columns: repeat(2, minmax(260px, 1fr));
  }
}

// 移动端改为单列，并撤销日本账户单行和 PingPong 左右栏的桌面布局。
@media (max-width: 700px) {
  .account-grid {
    grid-template-columns: 1fr;
  }

  .account-section-japan {
    .account-grid {
      flex-wrap: wrap;
    }

    .account-card {
      width: 100%;
      margin-right: 0;
    }
  }

  .account-card.wide-card {
    grid-column: span 1;
  }

  .wide-card .balance-list.multiple-balances {
    grid-template-columns: 1fr;
  }

  .wide-card .balance-list.three-balances .balance-item:nth-child(2) {
    grid-column: auto;
    grid-row: auto;
    margin-left: 0;
    border-left: 0;

    &::before {
      display: block;
    }
  }
}
</style>
